<template>
  <div class="musubi-page musubi-page--lead-forms">
    <header class="musubi-page__head">
      <h1>Lead Forms</h1>
      <p class="text-muted">
        Embed lead-capture forms on landing pages (add a <code>form</code> block with
        the form's ID). Submissions create or update a Subscriber, add them to the
        target list, and optionally enrol them in a drip flow.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <!-- Toolbar -->
    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="showCreateForm = !showCreateForm">
        <CygnusIcon name="plus" class="me-1" />{{ showCreateForm ? 'Cancel' : 'New form' }}
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
    </section>

    <!-- Create form -->
    <section v-if="showCreateForm" class="create-form card mb-3">
      <div class="card-body">
        <h6 class="mb-3">New lead form</h6>
        <form @submit.prevent="createForm">
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label small text-muted">Name *</label>
              <input v-model="newForm.name" type="text" required class="form-control form-control-sm" placeholder="Daedalus early access">
            </div>
            <div class="col-md-6">
              <label class="form-label small text-muted">Slug *</label>
              <input v-model="newForm.slug" type="text" required class="form-control form-control-sm" placeholder="daedalus-early-access">
            </div>
          </div>
          <div class="d-flex gap-2 mt-3">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.create">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.create" />
              <span v-else>Create</span>
            </button>
          </div>
        </form>
      </div>
    </section>

    <!-- Detail / editor panel -->
    <section v-if="selected" class="lf-detail card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 class="mb-1">{{ selected.name }}</h5>
            <div class="small text-muted">
              slug: <code>{{ selected.slug }}</code>
              &nbsp;·&nbsp;
              <span class="badge bg-secondary">{{ capturedCount }} captured</span>
            </div>
          </div>
          <button class="btn btn-link btn-sm" @click="closeDetail">Close</button>
        </div>

        <!-- Basic fields -->
        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <label class="form-label small text-muted">Name</label>
            <input v-model="editForm.name" type="text" class="form-control form-control-sm">
          </div>
          <div class="col-md-6">
            <label class="form-label small text-muted">Slug</label>
            <input v-model="editForm.slug" type="text" class="form-control form-control-sm">
          </div>
          <div class="col-md-6">
            <label class="form-label small text-muted">Submit button label</label>
            <input v-model="editForm.submit_label" type="text" class="form-control form-control-sm" placeholder="Subscribe">
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted">List</label>
            <MusubiEntitySelect
              v-model="editForm.list_id"
              endpoint="/api/v1/admin/lists"
              :option-label="l => l.name"
              placeholder="(none)" />
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted">Drip flow</label>
            <MusubiEntitySelect
              v-model="editForm.drip_flow_id"
              endpoint="/api/v1/admin/drip-flows"
              :option-label="f => f.name"
              placeholder="(none)" />
          </div>
        </div>

        <!-- After-submit behaviour -->
        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <label class="form-label small text-muted">Success message</label>
            <input v-model="editForm.success_message" type="text" class="form-control form-control-sm" placeholder="Thanks — you're subscribed.">
          </div>
          <div class="col-md-6">
            <label class="form-label small text-muted">Redirect URL <span class="text-muted">(overrides success message)</span></label>
            <input v-model="editForm.redirect_url" type="url" class="form-control form-control-sm" placeholder="https://…">
          </div>
          <div class="col-12">
            <div class="form-check">
              <input v-model="editForm.double_optin" type="checkbox" class="form-check-input" id="double-optin">
              <label class="form-check-label small" for="double-optin">Double opt-in (send confirmation email before activating)</label>
            </div>
          </div>
        </div>

        <!-- Field builder -->
        <div class="mb-3">
          <label class="form-label small text-muted fw-bold">Form fields</label>
          <div class="small text-muted mb-2">
            <code>email</code> is always included. Add extra fields below.
          </div>

          <div v-if="editForm.fields.length === 0" class="text-muted small fst-italic mb-2">
            No extra fields — form will collect email only.
          </div>

          <div
            v-for="(field, idx) in editForm.fields"
            :key="idx"
            class="field-row"
          >
            <input
              v-model="field.name"
              type="text"
              class="form-control form-control-sm"
              placeholder="field_name"
            >
            <input
              v-model="field.label"
              type="text"
              class="form-control form-control-sm"
              placeholder="Label"
            >
            <select v-model="field.type" class="form-select form-select-sm">
              <option value="text">text</option>
              <option value="email">email</option>
              <option value="tel">tel</option>
              <option value="number">number</option>
              <option value="textarea">textarea</option>
            </select>
            <div class="form-check d-flex align-items-center">
              <input v-model="field.required" type="checkbox" class="form-check-input me-1" :id="`req-${idx}`">
              <label class="form-check-label small" :for="`req-${idx}`">Required</label>
            </div>
            <button
              type="button"
              class="btn btn-link btn-sm text-danger p-0"
              title="Remove field"
              @click="removeField(idx)"
            >
              <CygnusIcon name="x" />
            </button>
          </div>

          <button type="button" class="btn btn-sm btn-outline-secondary mt-2" @click="addField">
            <CygnusIcon name="plus" class="me-1" />Add field
          </button>
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-primary btn-sm" :disabled="busy.save" @click="saveForm">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.save" />
            <span v-else><CygnusIcon name="download" class="me-1" />Save</span>
          </button>
          <button class="btn btn-outline-danger btn-sm" @click="deleteForm(selected.id)">
            <CygnusIcon name="trash" class="me-1" />Delete
          </button>
        </div>

        <p v-if="saveMessage" :class="['mt-2 small', saveOk ? 'text-success' : 'text-danger']">{{ saveMessage }}</p>
      </div>
    </section>

    <!-- List -->
    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      storage-key="admin.lead-forms"
      :initial-sort="{ column: 'created', dir: 'desc' }"
      :page-size="25"
      :loading="busy.load"
      :error="error"
      @row-click="onRowClick"
    />
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import MusubiEntitySelect from "@root/components/MusubiEntitySelect.vue";
import { esc } from "./util/cellHtml";

export default {
  name: 'AdminLeadForms',
  components: { MusubiTabulator, MusubiEntitySelect },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      selected: null,
      capturedCount: 0,
      editForm: { name: '', slug: '', submit_label: '', list_id: null, drip_flow_id: null, success_message: '', redirect_url: '', double_optin: false, fields: [] },
      busy: { load: false, create: false, save: false },
      error: null,
      saveMessage: '',
      saveOk: false,
      showCreateForm: false,
      newForm: { name: '', slug: '' },
    };
  },
  computed: {
    columns() {
      return [
        { key: 'id',           label: 'ID',       width: 60,  sorter: 'number' },
        { key: 'name',         label: 'Name' },
        { key: 'slug',         label: 'Slug',     width: 200, format: (v) => `<code>${esc(v)}</code>` },
        { key: 'list_id',      label: 'List',     width: 80,  sorter: 'number',
          format: (v) => v ? `#${esc(v)}` : '<span class="text-muted">—</span>' },
        { key: 'drip_flow_id', label: 'Drip',     width: 80,  sorter: 'number',
          format: (v) => v ? `#${esc(v)}` : '<span class="text-muted">—</span>' },
        { key: 'created',      label: 'Created',  width: 150, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
      ];
    },
  },
  mounted() { this.refresh(); },
  methods: {
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/lead-forms');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load lead forms.'; }
      this.busy.load = false;
    },
    async onRowClick(row) {
      if (!row?.id) return;
      try {
        const res = await this.api.get(`/api/v1/admin/lead-forms/${row.id}`);
        const payload = res?.data ?? res;
        this.selected = payload.form;
        this.capturedCount = payload.captured_count ?? 0;
        const fields = Array.isArray(payload.form.fields_json) ? payload.form.fields_json : [];
        this.editForm = {
          name: payload.form.name,
          slug: payload.form.slug,
          submit_label: payload.form.submit_label || 'Submit',
          list_id: payload.form.list_id ?? null,
          drip_flow_id: payload.form.drip_flow_id ?? null,
          success_message: payload.form.success_message || '',
          redirect_url: payload.form.redirect_url || '',
          double_optin: payload.form.double_optin === 1 || payload.form.double_optin === true,
          // Strip the auto-email row so we don't show a duplicate
          fields: fields.filter(f => f.name !== 'email'),
        };
        this.saveMessage = '';
      } catch (e) { this.error = e?.message || 'Failed to open form.'; }
    },
    closeDetail() { this.selected = null; this.saveMessage = ''; },
    addField() {
      this.editForm.fields.push({ name: '', label: '', type: 'text', required: false });
    },
    removeField(idx) {
      this.editForm.fields.splice(idx, 1);
    },
    async createForm() {
      this.busy.create = true; this.error = null;
      try {
        const res = await this.api.post('/api/v1/admin/lead-forms', {
          name: this.newForm.name,
          slug: this.newForm.slug,
        });
        const payload = res?.data ?? res;
        if (payload?.form?.id) {
          this.showCreateForm = false;
          this.newForm = { name: '', slug: '' };
          await this.refresh();
          await this.onRowClick({ id: payload.form.id });
        } else {
          this.error = payload?.error || 'Create failed.';
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Create failed.';
      }
      this.busy.create = false;
    },
    async saveForm() {
      this.busy.save = true; this.saveMessage = ''; this.saveOk = false;
      try {
        // Always prepend email as the first field
        const emailField = { name: 'email', label: 'Email', type: 'email', required: true };
        const fields_json = [emailField, ...this.editForm.fields.filter(f => f.name && f.name !== 'email')];
        const body = {
          name: this.editForm.name,
          slug: this.editForm.slug,
          submit_label: this.editForm.submit_label || 'Submit',
          list_id: this.editForm.list_id || null,
          drip_flow_id: this.editForm.drip_flow_id || null,
          success_message: this.editForm.success_message || null,
          redirect_url: this.editForm.redirect_url || null,
          double_optin: this.editForm.double_optin ? 1 : 0,
          fields_json,
        };
        const res = await this.api.put(`/api/v1/admin/lead-forms/${this.selected.id}`, body);
        const payload = res?.data ?? res;
        if (payload?.form) {
          this.selected = payload.form;
          this.saveOk = true;
          this.saveMessage = 'Saved.';
          await this.refresh();
        } else {
          this.saveMessage = payload?.error || 'Save failed.';
        }
      } catch (e) {
        this.saveMessage = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.save = false;
    },
    async deleteForm(id) {
      if (!confirm('Delete this lead form? Existing submissions are retained for audit.')) return;
      try {
        await this.api.delete(`/api/v1/admin/lead-forms/${id}`);
        this.closeDetail();
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Delete failed.';
      }
    },
  },
};
</script>

<style>
.musubi-page--lead-forms { padding: var(--m-space-6, 32px); }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.lf-detail { background: var(--m-bg-card, #FFF); }

.field-row {
  display: grid;
  grid-template-columns: 160px 1fr 100px 90px 30px;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
}
</style>
